/**
 * The prestige offer mail's two links (AD-779 S5; spec
 * docs/systems/prestige-cosmetic-acquisition-2026-10.md §11.2 item 4 (ii)).
 *
 * Fragment-routed like the reset and verify links (`#/prestige-accept=<token>`,
 * `#/prestige-unsubscribe=<token>`; T-1: the token never reaches a server or proxy log). The
 * auth screen lifts the token, strips it from the address bar, and waits for ONE press:
 * opening the link must not spend the single-use token, because mailbox link scanners that
 * run page script open it before the player does (the AD-9002 lesson the verify link learnt).
 * Neither door logs the player in.
 */

import { ACCEPTED_HELD, ACCEPTED_OWNED, describeAcceptRefusal } from './panels/prestigeWords.js';

export interface PrestigeMailLink {
  kind: 'accept' | 'unsubscribe';
  token: string;
}

const LINK = /^#\/prestige-(accept|unsubscribe)=([0-9a-f]{16,200})$/;

/** The link the URL fragment carries, or null. */
export function parsePrestigeMailLink(hash: string): PrestigeMailLink | null {
  const m = LINK.exec(hash);
  if (!m || !m[1] || !m[2]) return null;
  return { kind: m[1] === 'accept' ? 'accept' : 'unsubscribe', token: m[2] };
}

export const MAIL_ACCEPT_INTRO = 'The Great Cloud has offered you a prestige cosmetic.';
export const MAIL_ACCEPT_BUTTON = 'Accept the offer';
export const MAIL_UNSUBSCRIBE_INTRO =
  'Stop offer emails for this account? Offers still appear in the game when you log in.';
export const MAIL_UNSUBSCRIBE_BUTTON = 'Stop offer emails';
export const MAIL_UNSUBSCRIBED = 'Done. You will get no more offer emails.';
export const MAIL_LINK_INVALID =
  'This link has expired or was already used. Log in to see any offer that is still open.';

/** The words for an accept answer, by the server's `held` flag. */
export function acceptedWords(held: boolean): string {
  return held ? ACCEPTED_HELD : ACCEPTED_OWNED;
}

/** The words for a refused accept: a dead link, else S3's refusal words for the code. */
export function refusedWords(code: string | undefined): string {
  if (code === 'tokenInvalid') return MAIL_LINK_INVALID;
  return describeAcceptRefusal(code ?? '');
}

/** Every string this module can put on screen (the name guard reads them). */
export const PRESTIGE_MAIL_LINK_STRINGS: readonly string[] = [
  MAIL_ACCEPT_INTRO,
  MAIL_ACCEPT_BUTTON,
  MAIL_UNSUBSCRIBE_INTRO,
  MAIL_UNSUBSCRIBE_BUTTON,
  MAIL_UNSUBSCRIBED,
  MAIL_LINK_INVALID,
];
